import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  createTaxTableApi,
  deleteTaxTableApi,
  DpTaxTable,
  DpTaxTableType,
  listTaxTablesApi,
  TAX_TABLE_TYPE_LABELS,
  TaxTablePayload,
  updateTaxTableApi,
} from "../api/payrollApi";
import { formatCurrency, formatDateOnly, formatPercentValue, toDateInputValue } from "../utils/format";

type BracketRow = { upTo: string; ratePercent: string; deduction: string };
type Form = {
  type: DpTaxTableType;
  effectiveFrom: string;
  dependentDeduction: string;
  simplifiedDiscount: string;
  notes: string;
  brackets: BracketRow[];
};

const TYPES: DpTaxTableType[] = ["INSS", "IRRF", "IRRF_REDUCAO"];

/** Rótulos das colunas de faixa: a redução do IR lê os mesmos três números de outro jeito. */
const COLUMN_LABELS: Record<DpTaxTableType, [string, string, string]> = {
  INSS: ["Salário até (R$)", "Alíquota (%)", "Parcela a deduzir (R$)"],
  IRRF: ["Base até (R$)", "Alíquota (%)", "Parcela a deduzir (R$)"],
  IRRF_REDUCAO: ["Rendimento até (R$)", "Fator (%)", "Parcela (R$)"],
};

const TYPE_HINTS: Record<DpTaxTableType, string> = {
  INSS: "Desconto = salário × alíquota − parcela, na faixa em que o salário cai. O limite da última faixa é o teto.",
  IRRF: "Imposto = base × alíquota − parcela. Deixe vazio o limite da última faixa (sem limite).",
  IRRF_REDUCAO:
    "Redução = parcela − fator × rendimento bruto, limitada ao imposto. Ex.: até 5.000 → fator 0 e parcela 312,89; até 7.350 → fator 13,3145 e parcela 978,62; última faixa sem limite, 0 e 0.",
};

const emptyRow = (): BracketRow => ({ upTo: "", ratePercent: "", deduction: "0" });
const toInput = (value: string | null) => (value === null ? "" : String(Number(value)));

const formFrom = (table: DpTaxTable, asNewVersion = false): Form => ({
  type: table.type,
  effectiveFrom: asNewVersion ? "" : toDateInputValue(table.effectiveFrom),
  dependentDeduction: toInput(table.dependentDeduction),
  simplifiedDiscount: toInput(table.simplifiedDiscount),
  notes: asNewVersion ? "" : table.notes ?? "",
  brackets: table.brackets.map((b) => ({
    upTo: toInput(b.upTo),
    ratePercent: String(Number(b.ratePercent)),
    deduction: String(Number(b.deduction)),
  })),
});

/** Aba "Tabelas de INSS e IR" em Cadastros (folha para conferência, ADR 0016). */
export default function TaxTablesSettings({ token }: { token: string }) {
  const [tables, setTables] = useState<DpTaxTable[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState<Form | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTables(await listTaxTablesApi(token));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar as tabelas.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  /** Nova vigência parte da tabela mais recente do tipo — no reajuste anual, muda-se só o que mudou. */
  const openNew = (type: DpTaxTableType) => {
    const latest = tables.find((t) => t.type === type);
    setForm(
      latest
        ? formFrom(latest, true)
        : { type, effectiveFrom: "", dependentDeduction: "", simplifiedDiscount: "", notes: "", brackets: [emptyRow()] }
    );
    setModal({});
  };

  const openEdit = (table: DpTaxTable) => {
    setForm(formFrom(table));
    setModal({ id: table.id });
  };

  const setRow = (index: number, patch: Partial<BracketRow>) =>
    setForm((f) => (f ? { ...f, brackets: f.brackets.map((b, i) => (i === index ? { ...b, ...patch } : b)) } : f));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !modal) return;
    if (!form.effectiveFrom) return toast.error("Informe a data a partir da qual a tabela vale.");

    const num = (value: string) => (value.trim() === "" ? null : Number(value));
    const payload: TaxTablePayload = {
      type: form.type,
      effectiveFrom: form.effectiveFrom,
      dependentDeduction: form.type === "IRRF" ? num(form.dependentDeduction) : null,
      simplifiedDiscount: form.type === "IRRF" ? num(form.simplifiedDiscount) : null,
      notes: form.notes.trim() || undefined,
      brackets: form.brackets.map((b) => ({
        upTo: num(b.upTo),
        ratePercent: Number(b.ratePercent || 0),
        deduction: Number(b.deduction || 0),
      })),
    };

    setSaving(true);
    try {
      if (modal.id) await updateTaxTableApi(token, modal.id, payload);
      else await createTaxTableApi(token, payload);
      toast.success("Tabela salva. Competências abertas usam o novo valor ao recalcular.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar a tabela.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (table: DpTaxTable) => {
    if (!window.confirm(`Excluir a tabela de ${TAX_TABLE_TYPE_LABELS[table.type]} de ${formatDateOnly(table.effectiveFrom)}?`)) return;
    try {
      await deleteTaxTableApi(token, table.id);
      toast.success("Tabela excluída.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir a tabela.");
    }
  };

  return (
    <>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        Tabelas usadas pela folha de conferência, cada uma com a data em que passa a valer. As de 2026 já vêm
        cadastradas; a cada portaria, cadastre a nova vigência. O INSS usa a tabela do mês trabalhado; o IR, a da
        data do pagamento.
      </p>

      {loading ? <div className="dpEmpty">Carregando...</div> : null}

      {!loading &&
        TYPES.map((type) => {
          const ofType = tables.filter((t) => t.type === type);
          const [colUpTo, colRate, colDeduction] = COLUMN_LABELS[type];
          return (
            <section key={type} className="dpSection">
              <div className="dpHeader" style={{ marginBottom: 12 }}>
                <h3 className="dpSection__title" style={{ margin: 0 }}>
                  {TAX_TABLE_TYPE_LABELS[type]}
                </h3>
                <button className="dpBtn dpBtn--secondary" onClick={() => openNew(type)}>
                  Nova vigência
                </button>
              </div>

              {ofType.length === 0 ? (
                <p className="dpNotice dpNotice--warning">Nenhuma tabela cadastrada: o cálculo fica pendente.</p>
              ) : (
                ofType.map((table) => (
                  <div key={table.id} style={{ marginBottom: 16 }}>
                    <div className="dpControls" style={{ marginBottom: 8 }}>
                      <strong>
                        {formatDateOnly(table.effectiveFrom)} —{" "}
                        {table.effectiveTo ? formatDateOnly(table.effectiveTo) : "vigente"}
                      </strong>
                      {table.type === "IRRF" ? (
                        <span className="dpFormHint">
                          Dependente {formatCurrency(table.dependentDeduction)} · simplificado{" "}
                          {formatCurrency(table.simplifiedDiscount)}
                        </span>
                      ) : null}
                      {table.notes ? <span className="dpFormHint">{table.notes}</span> : null}
                      <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openEdit(table)}>
                        Editar
                      </button>
                      <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => remove(table)}>
                        Excluir
                      </button>
                    </div>
                    <div className="dpTableWrapper">
                      <table className="dpTable">
                        <thead>
                          <tr>
                            <th>{colUpTo}</th>
                            <th>{colRate}</th>
                            <th>{colDeduction}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {table.brackets.map((b) => (
                            <tr key={b.id}>
                              <td>{b.upTo === null ? "sem limite" : formatCurrency(b.upTo)}</td>
                              <td>{formatPercentValue(b.ratePercent)}</td>
                              <td>{formatCurrency(b.deduction)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </section>
          );
        })}

      {modal && form ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {modal.id ? "Editar" : "Nova vigência"}: {TAX_TABLE_TYPE_LABELS[form.type]}
              </h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <p className="dpNotice" style={{ margin: 0 }}>
                  {TYPE_HINTS[form.type]}
                </p>
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="taxFrom">Vigente a partir de *</label>
                    <input
                      id="taxFrom"
                      type="date"
                      className="dpInput"
                      value={form.effectiveFrom}
                      onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  {form.type === "IRRF" ? (
                    <>
                      <div className="dpFormGroup">
                        <label htmlFor="taxDependent">Dedução por dependente (R$) *</label>
                        <input
                          id="taxDependent"
                          type="number"
                          step="0.01"
                          min="0"
                          className="dpInput"
                          value={form.dependentDeduction}
                          onChange={(e) => setForm({ ...form, dependentDeduction: e.target.value })}
                          disabled={saving}
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label htmlFor="taxSimplified">Desconto simplificado (R$) *</label>
                        <input
                          id="taxSimplified"
                          type="number"
                          step="0.01"
                          min="0"
                          className="dpInput"
                          value={form.simplifiedDiscount}
                          onChange={(e) => setForm({ ...form, simplifiedDiscount: e.target.value })}
                          disabled={saving}
                        />
                      </div>
                    </>
                  ) : null}
                </div>

                <div className="dpTableWrapper">
                  <table className="dpTable">
                    <thead>
                      <tr>
                        {COLUMN_LABELS[form.type].map((label) => (
                          <th key={label}>{label}</th>
                        ))}
                        <th style={{ width: 90 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.brackets.map((b, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="dpInput"
                              placeholder={index === form.brackets.length - 1 ? "sem limite" : ""}
                              value={b.upTo}
                              onChange={(e) => setRow(index, { upTo: e.target.value })}
                              disabled={saving}
                              aria-label={`Limite da faixa ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              max="100"
                              className="dpInput"
                              value={b.ratePercent}
                              onChange={(e) => setRow(index, { ratePercent: e.target.value })}
                              disabled={saving}
                              aria-label={`Alíquota da faixa ${index + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="dpInput"
                              value={b.deduction}
                              onChange={(e) => setRow(index, { deduction: e.target.value })}
                              disabled={saving}
                              aria-label={`Parcela da faixa ${index + 1}`}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="dpBtn dpBtn--danger dpBtn--small"
                              onClick={() => setForm({ ...form, brackets: form.brackets.filter((_, i) => i !== index) })}
                              disabled={saving || form.brackets.length === 1}
                            >
                              Remover
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  className="dpBtn dpBtn--secondary dpBtn--small"
                  style={{ alignSelf: "flex-start" }}
                  onClick={() => setForm({ ...form, brackets: [...form.brackets, emptyRow()] })}
                  disabled={saving}
                >
                  Adicionar faixa
                </button>

                <div className="dpFormGroup">
                  <label htmlFor="taxNotes">Observação</label>
                  <input
                    id="taxNotes"
                    className="dpInput"
                    maxLength={500}
                    placeholder="Ex.: a portaria que fixou os valores"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
